import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";
import {
    Lightbulb,
    TrendingUp,
    TrendingDown,
    Target,
    AlertTriangle,
    CheckCircle2,
    Info,
    Percent,
    Wallet,
    ArrowUpRight,
} from "lucide-react";
import { AppShell, PageHeader } from "../components/AppShell";
import { TrafficLight } from "../components/TrafficLight";
import { CriticalBreakdownToggle } from "../components/CriticalBreakdown";
import { BestSupplierSection } from "../components/BestSupplierSection";
import { api } from "../lib/api";
import { catColor, catLabel, euro, foodCostColor, recipeCatColor } from "../lib/constants";

export default function Stats() {
    const [data, setData] = useState(null);
    const [bestSupplier, setBestSupplier] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let alive = true;
        api.get("/stats")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        api.get("/stats/best-supplier")
            .then(({ data }) => { if (alive) setBestSupplier(data); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <AppShell>
                <div className="py-16 text-center text-sm text-brand-muted">Caricamento…</div>
            </AppShell>
        );
    }

    if (!data) {
        return (
            <AppShell>
                <PageHeader eyebrow="Analytics" title="Statistiche" />
                <div className="bg-paper rounded-xl border border-dashed border-brand-line p-10 text-center text-sm text-brand-muted">
                    Nessun dato disponibile.
                </div>
            </AppShell>
        );
    }

    const m = data.metrics;

    return (
        <AppShell>
            <PageHeader
                eyebrow="Analytics"
                title="Statistiche"
                subtitle="Una vista d'insieme sul tuo menù: semafori, grafici, indici e consigli per ottimizzare la marginalità."
            />

            {/* Semafori (distribuzione food cost) */}
            <section className="mb-10 animate-fade-up">
                <h2 className="font-display text-2xl text-brand-ink mb-4">Semafori food cost</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fc-semafori">
                    <TrafficStat label="Ottimo" sublabel="<30%" count={m.healthy_recipes} color="#2e7d32" />
                    <TrafficStat label="Attenzione" sublabel="30–35%" count={m.warning_recipes} color="#ed6c02" />
                    <TrafficStat label="Critico" sublabel="≥35%" count={m.critical_recipes} color="#d32f2f" />
                    <TrafficStat label="Senza prezzo" sublabel="da impostare" count={m.unpriced_recipes} color="#9e9e9e" />
                </div>
            </section>

            <BestSupplierSection items={bestSupplier} />

            {/* Indici */}
            <section className="mb-10">
                <h2 className="font-display text-2xl text-brand-ink mb-4">Indici chiave</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <IndexCard
                        icon={<Percent size={18} />}
                        tint={foodCostColor(m.avg_food_cost_pct)}
                        label="Food cost medio"
                        value={m.avg_food_cost_pct ? `${m.avg_food_cost_pct.toFixed(1)}%` : "—"}
                        sub={`su ${m.priced_recipes} piatti prezzati`}
                        testid="idx-avg-fc"
                    />
                    <IndexCard
                        icon={<ArrowUpRight size={18} />}
                        tint="#c9a961"
                        label="Recupero medio / porzione"
                        value={euro(m.margin_gap_avg_per_portion)}
                        sub={
                            m.critical_recoverable > 0
                                ? `su ${m.critical_recoverable} piatti recuperabili`
                                : "nessun piatto critico recuperabile"
                        }
                        testid="idx-margin-gap"
                    />
                    <IndexCard
                        icon={<Wallet size={18} />}
                        tint="#0288d1"
                        label="Totale acquisti"
                        value={euro(m.total_purchases)}
                        sub={`${m.purchases_count} movimenti`}
                        testid="idx-total-purchases"
                    />
                    <IndexCard
                        icon={<Target size={18} />}
                        tint="#2e7d32"
                        label="Miglior piatto"
                        value={m.best_recipe ? m.best_recipe.name : "—"}
                        sub={m.best_recipe ? `${m.best_recipe.food_cost_pct.toFixed(1)}% FC` : ""}
                        onClick={m.best_recipe ? () => navigate(`/recipes/${m.best_recipe.id}`) : null}
                        testid="idx-best"
                    />
                </div>
            </section>

            {/* Torta grafici */}
            <section className="mb-10">
                <h2 className="font-display text-2xl text-brand-ink mb-4">Distribuzioni</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <ChartCard title="Piatti per food cost" testid="chart-fc">
                        <PieChartComp
                            data={data.fc_distribution.filter((d) => d.value > 0)}
                            colorKey="color"
                            valueLabel="piatti"
                        />
                    </ChartCard>
                    <ChartCard title="Piatti per categoria" testid="chart-recipes-cat">
                        <PieChartComp
                            data={data.recipes_by_category.map((d) => ({
                                ...d,
                                color: recipeCatColor(d.name),
                                displayName: d.name,
                            }))}
                            valueLabel="piatti"
                        />
                    </ChartCard>
                    <ChartCard title="Spesa per categoria" testid="chart-spend-cat">
                        {data.spend_by_category.length === 0 ? (
                            <EmptyChart text="Nessun acquisto registrato." />
                        ) : (
                            <PieChartComp
                                data={data.spend_by_category.map((d) => ({
                                    ...d,
                                    color: catColor(d.name),
                                    displayName: catLabel(d.name),
                                }))}
                                valueLabel="€"
                                currency
                            />
                        )}
                    </ChartCard>
                </div>
            </section>

            {/* Top fornitori */}
            {data.top_suppliers.length > 0 && (
                <section className="mb-10">
                    <h2 className="font-display text-2xl text-brand-ink mb-4">Top fornitori per spesa</h2>
                    <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden" data-testid="top-suppliers">
                        {data.top_suppliers.map((s, i) => {
                            const pct = m.total_purchases > 0 ? (s.value / m.total_purchases) * 100 : 0;
                            return (
                                <div
                                    key={s.name}
                                    className={`flex items-center gap-4 px-5 py-4 ${i > 0 ? "border-t border-brand-line" : ""}`}
                                >
                                    <div className="h-9 w-9 rounded-full bg-brand-gold/15 text-brand-goldDark flex items-center justify-center font-display text-sm shrink-0">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-brand-ink truncate">{s.name}</div>
                                        <div className="h-1.5 mt-2 rounded-full bg-brand-cream overflow-hidden">
                                            <div
                                                className="h-full rounded-full"
                                                style={{
                                                    width: `${Math.min(100, pct)}%`,
                                                    background: "#c9a961",
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display text-xl text-brand-ink leading-none">{euro(s.value)}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-brand-muted mt-1">
                                            {pct.toFixed(1)}% del totale
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Piatti fuori controllo (severi) */}
            {data.critical_details && data.critical_details.some((d) => d.classification === "severe") && (
                <section className="mb-10">
                    <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle size={18} className="text-brand-danger" />
                        <h2 className="font-display text-2xl text-brand-ink">Piatti fuori controllo</h2>
                    </div>
                    <div className="bg-paper rounded-xl border border-brand-line shadow-card overflow-hidden" data-testid="severe-list">
                        {data.critical_details
                            .filter((d) => d.classification === "severe")
                            .sort((a, b) => b.increase_needed_pct - a.increase_needed_pct)
                            .map((d, i) => (
                                <div
                                    key={d.id}
                                    className={`px-5 py-4 ${i > 0 ? "border-t border-brand-line" : ""}`}
                                    data-testid={`severe-${d.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <button
                                            onClick={() => navigate(`/recipes/${d.id}`)}
                                            className="text-left flex-1 min-w-0 group"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                                    style={{ background: "#d32f2f", boxShadow: "0 0 8px #d32f2f88" }}
                                                />
                                                <div className="font-display text-xl text-brand-ink leading-tight truncate group-hover:text-brand-gold transition">
                                                    {d.name}
                                                </div>
                                            </div>
                                            <div className="mt-1 text-[11px] uppercase tracking-wider text-brand-muted">
                                                Food cost {d.food_cost_pct.toFixed(1)}% · prezzo attuale {euro(d.selling_price)}
                                            </div>
                                        </button>
                                        <div
                                            className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
                                            style={{ background: "rgba(211,47,47,0.1)", color: "#d32f2f" }}
                                        >
                                            +{d.increase_needed_pct.toFixed(0)}% per FC 30%
                                        </div>
                                    </div>
                                    <div className="mt-3 rounded-lg bg-brand-cream border border-brand-line px-4 py-3">
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-brand-goldDark font-bold mb-1">
                                            Non alzare il prezzo — prova invece
                                        </div>
                                        <ul className="text-[13px] text-brand-ink space-y-1">
                                            <li>🔄 <strong>Cambia fornitore</strong> sugli ingredienti più costosi del piatto</li>
                                            <li>📏 <strong>Riduci la porzione</strong> mantenendo il prezzo attuale</li>
                                            <li>🗑️ <strong>Rimuovi il piatto</strong> dal menù se non è strategico</li>
                                        </ul>
                                    </div>
                                    <CriticalBreakdownToggle details={d} />
                                </div>
                            ))}
                    </div>
                </section>
            )}

            {/* Consigli */}
            <section className="mb-12">
                <div className="flex items-center gap-2 mb-4">
                    <Lightbulb size={18} className="text-brand-gold" />
                    <h2 className="font-display text-2xl text-brand-ink">Consigli</h2>
                </div>
                {data.tips.length === 0 ? (
                    <div className="bg-paper rounded-xl border border-dashed border-brand-line p-8 text-center text-sm text-brand-muted">
                        Il tuo menù sembra in ottima forma. Nessun consiglio al momento.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tips-list">
                        {data.tips.map((t, i) => (
                            <TipCard key={i} tip={t} />
                        ))}
                    </div>
                )}
            </section>
        </AppShell>
    );
}

function TrafficStat({ label, sublabel, count, color }) {
    return (
        <div
            className="bg-paper rounded-xl border border-brand-line shadow-card p-5"
            style={{ borderLeft: `4px solid ${color}` }}
        >
            <div className="flex items-center gap-2">
                <span
                    className="h-3 w-3 rounded-full"
                    style={{ background: color, boxShadow: `0 0 8px ${color}88` }}
                />
                <div className="eyebrow text-brand-ink/60">{label}</div>
            </div>
            <div className="mt-2 font-display text-4xl text-brand-ink leading-none">{count}</div>
            <div className="mt-1 text-[11px] uppercase tracking-wider text-brand-muted">{sublabel}</div>
        </div>
    );
}

function IndexCard({ icon, tint, label, value, sub, onClick, testid }) {
    const Wrap = onClick ? "button" : "div";
    return (
        <Wrap
            onClick={onClick}
            data-testid={testid}
            className={`text-left bg-paper rounded-xl border border-brand-line shadow-card p-5 transition-all duration-300 ${
                onClick ? "hover:shadow-cardHover hover:border-brand-gold/50 cursor-pointer" : ""
            }`}
        >
            <div className="flex items-center gap-3">
                <div
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ background: `${tint}1a`, color: tint }}
                >
                    {icon}
                </div>
                <div className="eyebrow text-brand-ink/60">{label}</div>
            </div>
            <div className="mt-3 font-display text-2xl text-brand-ink leading-tight truncate">{value}</div>
            {sub && <div className="mt-1 text-[11px] uppercase tracking-wider text-brand-muted">{sub}</div>}
        </Wrap>
    );
}

function ChartCard({ title, children, testid }) {
    return (
        <div className="bg-paper rounded-xl border border-brand-line shadow-card p-5" data-testid={testid}>
            <div className="eyebrow text-brand-ink/60 mb-3">{title}</div>
            <div style={{ width: "100%", height: 280 }}>{children}</div>
        </div>
    );
}

function PieChartComp({ data, valueLabel, currency = false }) {
    if (!data || data.length === 0) return <EmptyChart />;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie
                    data={data}
                    dataKey="value"
                    nameKey="displayName"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={2}
                    stroke="#ffffff"
                    strokeWidth={2}
                >
                    {data.map((d, idx) => (
                        <Cell key={idx} fill={d.color || "#c9a961"} />
                    ))}
                </Pie>
                <Tooltip
                    contentStyle={{
                        background: "#1a2a44",
                        border: "none",
                        borderRadius: 8,
                        color: "#fff",
                        fontFamily: "Manrope, sans-serif",
                        fontSize: 12,
                    }}
                    formatter={(value, name) => [
                        currency ? `€${Number(value).toFixed(2)}` : `${value} ${valueLabel || ""}`.trim(),
                        name,
                    ]}
                />
                <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11, fontFamily: "Manrope, sans-serif", color: "#6b7280" }}
                    formatter={(value) => <span style={{ color: "#0f1b2e" }}>{value}</span>}
                />
            </PieChart>
        </ResponsiveContainer>
    );
}

function EmptyChart({ text = "Dati insufficienti" }) {
    return (
        <div className="h-full w-full flex items-center justify-center text-sm text-brand-muted italic">
            {text}
        </div>
    );
}

const TIP_STYLES = {
    danger: { border: "#d32f2f", bg: "rgba(211,47,47,0.06)", icon: <AlertTriangle size={18} />, color: "#d32f2f" },
    warning: { border: "#ed6c02", bg: "rgba(237,108,2,0.06)", icon: <AlertTriangle size={18} />, color: "#ed6c02" },
    success: { border: "#2e7d32", bg: "rgba(46,125,50,0.06)", icon: <CheckCircle2 size={18} />, color: "#2e7d32" },
    info: { border: "#0288d1", bg: "rgba(2,136,209,0.06)", icon: <Info size={18} />, color: "#0288d1" },
};

function TipCard({ tip }) {
    const s = TIP_STYLES[tip.severity] || TIP_STYLES.info;
    return (
        <div
            className="rounded-xl p-5 border bg-paper shadow-card flex gap-4"
            style={{ borderLeft: `4px solid ${s.border}` }}
        >
            <div
                className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                style={{ background: s.bg, color: s.color }}
            >
                {s.icon}
            </div>
            <div className="flex-1">
                <div className="font-display text-lg text-brand-ink leading-tight">{tip.title}</div>
                <div className="mt-1 text-sm text-brand-muted">{tip.text}</div>
            </div>
        </div>
    );
}
